import { computed, onMounted, reactive, ref, watch } from "vue";
import { useDisplay, useDefaults } from 'vuetify';
import vuetify from '@/Plugins/vuetify';

export type Density = null | 'default' | 'comfortable' | 'compact';
export type TextFieldVariant =  "filled" | "underlined" | "outlined" | "plain" | "solo" | "solo-inverted" | "solo-filled";
export type BtnVariant =  'flat' | 'text' | 'elevated' | 'tonal' | 'outlined' | 'plain';
export const pageTitle = ref("");
export const isDesktop = ref(false);
export const isMobile = ref(false);
export const isTablet = ref(false);
export function useTouch() {
    const isTouch = ref(false);

    const checkTouchSupport = () => {
        isTouch.value = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    };

    checkTouchSupport();

    return { isTouch };
}
const { isTouch } = useTouch();
export const isTouchDevice = ref(isTouch);

export const sidebarDrawer = ref(false);

export const containerGapClass = ref(' pa-3 pa-md-5 px-lg-14 pt-lg-8 ');
export function resetContainerGapClass(){
    containerGapClass.value = ' pa-3 pa-md-5 px-lg-14 pt-lg-8 ';
}

export function containerGapForTable(fullPage: boolean = false){
    
    if(fullPage){
        containerGapClass.value = 'pa-0 pt-md-1 pl-md-1 container-fullpage-table ';
    }
    else{
        containerGapClass.value = ' pa-0 pt-3 pa-md-5 px-lg-14 pt-lg-8 ';
    }
}

export function containerGapForForm(){
    containerGapClass.value = ' pa-3 pa-md-5 px-lg-14 pt-lg-8 ';
}

export function containerGapForTabVertical(){
    containerGapClass.value = ' pa-2 pt-0 pa-md-5 px-lg-14 pt-lg-8';
}

export type TableCardOptions = {
    // for big screen
    fullPage?: boolean,
};

export function useTableCard(_defaults?: TableCardOptions){
    _defaults = _defaults ?? {};
    let defaults = {
        fullPage: _defaults?.fullPage ?? false,
    };
    const display = useDisplay();
    const belowBreakPoint = ref(false);
    const isFullPage = ref(defaults.fullPage);
    const tableDensity = ref<Density>('default');
    
    const card = reactive({
        class: '',
        rounded: 'lg',
        textClass: '',
        actionPad: 'px-3 px-md-0 pb-6',
        footerClass: 'flex-column pt-2',
    })

    const searchInput = reactive({
        label: 'Search',
        placeholder: '',
        variant: 'outlined' as TextFieldVariant,
        flat: true,
        density: 'compact' as Density,
        singleLine: true,
        class: ""
    });

    const actionMenu = reactive({
        btn:{
            variant: 'flat' as BtnVariant,
            color: 'on-surface',
            icon: 'more_vert',
            iconSize: '6',
        }
    });

    const paginationItemCount = computed(() : number => {
        let n = 3;
        if(display.lgAndUp.value){
            n = 10;
        }
        else if(display.mdAndUp.value){
            n = 7;
        }
        else if(display.smAndUp.value){
            n = 5;
        }
        return n;
    });

    const change = (isBigScreen: boolean = true) => {
        if(isBigScreen){
            tableDensity.value = 'default';
            searchInput.singleLine = false;
            searchInput.variant = 'outlined';
            searchInput.class = '';

            if(defaults.fullPage){
                card.class = '';
                card.rounded = '0';
                card.textClass = 'pa-0';
                card.actionPad = 'pa-4';
                card.footerClass = 'table-full-page flex-row justify-space-between pt-2 pb-2';
            }
            else{
                card.class = '';
                card.rounded = 'lg';
                card.textClass = '';
                card.actionPad = 'px-3 px-md-0 pb-6';
                card.footerClass = 'flex-column  justify-center pt-2';
            }
            
        }
        else{
            tableDensity.value = 'comfortable';
            searchInput.singleLine = true;
            searchInput.variant = 'solo-filled';
            searchInput.class = 'on-body';

            if(defaults.fullPage){
                card.class = 'bg-transparent no-shadow has-mobile-view';
                card.rounded = '0';
                card.textClass = 'pa-0';
                card.actionPad = 'pa-3';
                card.footerClass = 'table-full-page flex-column flex-sm-row justify-center justify-sm-space-between pt-2';
            }
            else{
                card.class = 'bg-transparent no-shadow has-mobile-view';
                card.rounded = '0';
                card.textClass = 'pa-0';
                card.actionPad = 'px-3 px-md-0 pb-3';
                card.footerClass = 'flex-column justify-center pt-2';
            }
        }
    } 

    watch(
        () => display.mdAndUp.value,
        (isBigScreen) => {
            change(isBigScreen);
            // change(false);
        },
        { immediate: true }
    );

    return {
        tableDensity,
        searchInput,
        card,
        actionMenu,
        isFullPage,
        paginationItemCount,
    }
}

// not used for now
export const layout = reactive({
    topbar:{
        opener:{
            icon:{
                size: '1'
            }
        }
    },
    container:{
        class: 'pa-5 px-lg-14 pt-lg-8',
    },
    formCard:{
        card:{
            class: '',
            title:{
                class: '',
            },
            text:{
                class: '',
                columns:{
                    class: '',
                },
                fieldGroup:{
                    class: '',
                }
            },
            action:{
                class: '',
                btn:{
                    class: 'match-input-compact',
                    submitBtn:{
                        color: 'primary',
                    },
                    backBtn:{
                        color: 'secondary',
                    },
                }
            }
        },
        field:{
            density: 'default' as Density,
            resDensity: 'default' as Density,
        },
        input:{
            variant: 'outlined' as TextFieldVariant,
            density: 'default' as Density,
        },
        checkbox:{
            density: 'default' as Density,
        },
        switch:{
            density: 'default' as Density,
        },
        select:{
            density: 'default' as Density,
        },
        textarea:{
            variant: 'outlined' as TextFieldVariant,
            density: 'default' as Density,
        },
        
    },
    tableCard:{
        card:{
            class: '',
            rounded: 'lg',
            text:{
                class: ''
            }
        },
        toolbar:{
            class: '',
            search: {
                input:{
                    label: 'Search',
                    placeholder: '',
                    variant: 'outlined' as TextFieldVariant,
                    flat: true,
                    density: 'compact' as Density,
                    singleLine: true,
                    class: '',
                }
            },
            action:{
                collapsed:{
                    btn:{
                        variant: 'flat' as BtnVariant,
                        color: 'on-surface',
                        icon: {
                            text: 'more_vert',
                            size: 6,
                        }
                    }
                },
                btn:{ 
                    class: 'match-input-compact table-action'
                }
            }
        },
        
        table:{
            density: 'default' as Density
        },
        pagination:{
            class: '', 
        }
    }
});

export type PageLayout = 'small-form' | 'big-form' | 'only-datatable' | 'form-then-datatable';
export function usePageLayout(type?: PageLayout){
    const display = useDisplay();
    console.log('vuetifyDefaults', vuetify)

    const change = (isBigScreen: boolean = true) => {
        if(vuetify.defaults.value){
            // console.log('vuetifyDefaults', vuetify)
            if(isBigScreen){
                // layout.formCard.field.resDensity = 'default';
                vuetify.defaults.value.VTextFieldV1.density = 'default';
                vuetify.defaults.value.VNumberInputV1.density = 'default';
                vuetify.defaults.value.VTextareaV1.density = 'default';
                vuetify.defaults.value.VSwitchV1.density = 'default';
                vuetify.defaults.value.VCheckboxV1.density = 'default';
                vuetify.defaults.value.VSelectV1.density = 'default';
                vuetify.defaults.value.VAutocompleteV1.density = 'default';
                vuetify.defaults.value.VBtnV1.class = 'match-input';
            }
            else{
                // layout.formCard.field.resDensity = 'comfortable';
                vuetify.defaults.value.VTextFieldV1.density = 'comfortable';
                vuetify.defaults.value.VNumberInputV1.density = 'comfortable';
                vuetify.defaults.value.VTextareaV1.density = 'comfortable';
                vuetify.defaults.value.VSwitchV1.density = 'comfortable';
                vuetify.defaults.value.VCheckboxV1.density = 'comfortable';
                vuetify.defaults.value.VSelectV1.density = 'comfortable';
                vuetify.defaults.value.VAutocompleteV1.density = 'comfortable';
                vuetify.defaults.value.VBtnV1.class = 'match-input-comfortable';
            }
        }
    }

    watch(
        () => display.mdAndUp.value,
        (isBigScreen) => {
            isDesktop.value = isBigScreen;
            change(isBigScreen);
            // change(false);
        },
        { immediate: true }
    );
    watch(
        () => display.sm.value,
        (newValue) => {
            isTablet.value = newValue;
        },
        { immediate: true }
    );
    watch(
        () => display.xs.value,
        (newValue) => {
            isMobile.value = newValue;
        },
        { immediate: true }
    );
}